import React, { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import ForgetPassword from "../pages/common/ForgetPassword";
import Login from "../pages/common/Login";
import OTPVerification from "../pages/common/OTPVerification";
import ResetPassword from "../pages/common/ResetPassword";
import AccountSetup from "../pages/common/AccountSetup";
import MerchantOnboardLayout from "../components/Merchant/MerchantOnboarding/MerchantOnboardLayout";
import Privacy from "../components/common/Privacy";
import CreateAccount from "../pages/Merchant/CreateAccount";
import AccountCreated from "../pages/Merchant/AccountCreated";
import NewMemberSetup from "../pages/User/NewMemberSetup";
import Subscription from "../pages/User/Subscription";
import FundraiserSubscription from "../pages/User/FundraiserSubscription";
import MembershipConfirmation from "../pages/User/MembershipConfirmation";
import MembershipActivated from "../pages/User/MembershipActivated";
import FundraiserBrand from "../pages/User/FundraiserBrand";
import OrderConfirmation from "../pages/User/OrderConfirmation";
import ExistingLogin from "../pages/User/ExistingLogin";
import UserForgetPassword from "../pages/User/ForgetPassword";
import UserOTPVerification from "../pages/User/OTPVerification";
import UserResetPassword from "../pages/User/ResetPassword";
import ExistingSubscription from "../pages/User/ExistingSubscription";
import SubscriptionFailure from "../pages/User/SubscriptionFailure";
import GiftCardsPage from "../pages/User/GiftCards";
import GiftCardDetailsComp from "../pages/User/GiftCardDetails";
import GiftCardSelectComp from "../pages/User/GiftCardsSelectComp";
import GiftCardEnterDetailsComp from "../pages/User/GidtCardEnterDetailsComp";
import GiftCardConfirmPayComp from "../pages/User/GiftCardConfirmPayComp";
import GiftCardThankYouComp from "../pages/User/GiftCardThankYouComp";
import GiftCardCancel from "../components/User/common/GiftCardCancel";
import Demosite from "../components/User/DemoSite/index";
import UserSubscription from "../components/User/UserSubMobile";
import ComingSoonPage from "../components/User/ComingSoon/ComingSoon";
import { useSelector, useDispatch } from "react-redux";
import { getOnboardingStatus } from "../services/fundraiserService";
import { getUserSubscription } from "../services/subscriptionService";
import { clearAuth } from "../redux/slices/authSlice";
import Loader from "../components/common/Loader";
import axios from "../utils/axios";

// Component to handle fundraiser redirect with Stripe check
const FundraiserRedirect = () => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state?.authReducer?.tokenData?.token);
  const [isChecking, setIsChecking] = useState(true);
  const [redirectPath, setRedirectPath] = useState(null);

  useEffect(() => {
    const checkStripeAndRedirect = async () => {
      const clearFundraiserSessionState = () => {
        sessionStorage.removeItem("showAccountPendingModal");
        sessionStorage.removeItem("fundraiser_stripe_complete");
        sessionStorage.removeItem("fundraiser_block_status");
        sessionStorage.removeItem("fundraiser_verified");
        sessionStorage.removeItem("fundraiser_waitlist_token");
      };

      // Check if we just verified during login - skip re-checking
      const alreadyVerified = sessionStorage.getItem('fundraiser_verified') === 'true';

      // If no token, redirect to login
      if (!token) {
        setRedirectPath("/fundraiser/account-login");
        setIsChecking(false);
        return;
      }

      // Skip verification if already done in login flow
      if (alreadyVerified) {
        // Already verified - go directly to dashboard
        setRedirectPath("/fundraiser/dashboard");
        setIsChecking(false);
        return;
      }

      try {
        const checkResponse = await axios.get("fundraiser/profile", {
          headers: {
            Authorization: `Bearer ${token}`,
            "api-key": process.env.REACT_APP_API_KEY,
            "Cache-Control": "no-cache",
          },
          params: { _: Date.now() },
        });

        const fundraiserProfile = checkResponse?.data?.data;
        const profileStatusRaw = fundraiserProfile?.status;
        const profileStatus = typeof profileStatusRaw === "string"
          ? profileStatusRaw.toLowerCase()
          : profileStatusRaw;
        const isApproved = profileStatus === "approved" || profileStatus === "live";

        if (!isApproved) {
          let blockStatus = "pending";
          if (profileStatus === "disabled") blockStatus = "disabled";
          else if (profileStatus === "deleted") blockStatus = "deleted";
          else if (profileStatus === "rejected") blockStatus = "rejected";
          else if (profileStatus === "declined") blockStatus = "declined";

          dispatch(clearAuth());
          clearFundraiserSessionState();
          if (token) {
            sessionStorage.setItem("fundraiser_waitlist_token", token);
          }
          sessionStorage.setItem("fundraiser_block_status", blockStatus);
          if (blockStatus === "pending" || blockStatus === "rejected" || blockStatus === "declined") {
            sessionStorage.setItem("showAccountPendingModal", "true");
          }
          if (blockStatus === "pending") {
            setRedirectPath("/fundraiser/coming-soon");
            // setRedirectPath("/fundraiser/account-login");
          } else {
            setRedirectPath("/fundraiser/account-login");
          }
          return;
        }

        const statusPromise = await dispatch(getOnboardingStatus()).unwrap();
        const detailsSubmitted = statusPromise?.data?.detailsSubmitted === true;
        const chargesEnabled = statusPromise?.data?.chargesEnabled === true;

        if (detailsSubmitted && chargesEnabled) {
          clearFundraiserSessionState();
          sessionStorage.setItem("fundraiser_stripe_complete", "true");
          setRedirectPath("/fundraiser/dashboard");
        } else if (detailsSubmitted && !chargesEnabled) {
          setRedirectPath("/complete-setup");
        } else {
          setRedirectPath("/fundraiser/welcome");
        }
      } catch (error) {
        dispatch(clearAuth());
        clearFundraiserSessionState();
        setRedirectPath("/fundraiser/account-login");
      } finally {
        setIsChecking(false);
      }
    };

    checkStripeAndRedirect();
  }, [dispatch, token]);

  if (isChecking) {
    return <Loader />;
  }

  return redirectPath ? <Navigate to={redirectPath} replace /> : <Loader />;
};

const MemberRedirect = () => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state?.authReducer?.tokenData?.token);
  const [isChecking, setIsChecking] = useState(true);
  const [redirectPath, setRedirectPath] = useState("/member-setup");

  useEffect(() => {
    const resolveMemberPath = async () => {
      if (!token) {
        setRedirectPath("/member-setup");
        setIsChecking(false);
        return;
      }

      try {
        const result = await dispatch(getUserSubscription()).unwrap();
        if (result?.success === true && Array.isArray(result?.data) && result.data.length > 0) {
          setRedirectPath("/existing-subscription");
        } else {
          setRedirectPath("/member-setup");
        }
      } catch (_) {
        setRedirectPath("/member-setup");
      } finally {
        setIsChecking(false);
      }
    };

    resolveMemberPath();
  }, [dispatch, token]);

  if (isChecking) {
    return <Loader />;
  }

  return <Navigate to={redirectPath} replace />;
};

const CommonRoutes = () => {
  const token = useSelector((state) => state?.authReducer?.tokenData?.token);
  const role = useSelector(
    (state) => state?.authReducer?.tokenData?.user?.role
  );

  const [isAuthenticated, setIsAuthenticated] = useState(!!token);
  const normalizedRole = typeof role === "string" ? role.toLowerCase() : role;
  const isMemberRole = normalizedRole === "user" || normalizedRole === "member";

  const getRedirectPath = () => {
    switch (normalizedRole) {
      case "admin":
        return "/admin/dashboard";
      case "merchant":
        return "/merchant/dashboard";
      case "fundraiser":
        return null;
      case "user":
      case "member":
        return "/";
      default:
        return "/";
    }
  };

  useEffect(() => {
    setIsAuthenticated(!!token);
  }, [token]);

  return (
    <Routes>
      <Route
        path="/"
        element={
          !isAuthenticated ? (
            <Login />
          ) : normalizedRole === "fundraiser" ? (
            <FundraiserRedirect />
          ) : isMemberRole ? (
            <MemberRedirect />
          ) : (
            <Navigate to={getRedirectPath()} replace />
          )
        }
      />
      <Route path="/complete-setup" element={<AccountSetup />} />
      <Route
        path="/dashboard"
        element={
          !isAuthenticated ? (
            <Navigate to="/" replace />
          ) : normalizedRole === "fundraiser" ? (
            <FundraiserRedirect />
          ) : isMemberRole ? (
            <MemberRedirect />
          ) : (
            <Navigate to={getRedirectPath()} replace />
          )
        }
      />
      {/* <Route
        path="/signup"
        element={
          !isAuthenticated ? (
            <SignUp />
          ) : normalizedRole === "fundraiser" ? (
            <FundraiserRedirect />
          ) : (
            <Navigate to={getRedirectPath()} />
          )
        }
      /> */}
      <Route path="/forget-password" element={<ForgetPassword />} />
      <Route path="/otp-verification" element={<OTPVerification />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy-policy" element={<Privacy />} />
      <Route path="/member-setup" element={<NewMemberSetup />} />
      <Route path="/subscription" element={<Subscription />} />
      <Route
        path="/fundraiser-subscription"
        element={<FundraiserSubscription />}
      />
      <Route
        path="/membership-confirmation"
        element={<MembershipConfirmation />}
      />
      <Route
        path="/membership-activated"
        element={<MembershipActivated />}
      />
      <Route path="/fundraiser-brand/:id" element={<Navigate to="/fundraiser/:id" replace />} />
      <Route path="/success" element={<OrderConfirmation />} />
      <Route path="/existing-login" element={<ExistingLogin />} />
      <Route path="/user-forgetPassword" element={<UserForgetPassword />} />
      <Route path="/user-otpVerification" element={<UserOTPVerification />} />
      <Route path="/user-resetPassword" element={<UserResetPassword />} />
      <Route
        path="/existing-subscription"
        element={<ExistingSubscription />}
      />
      <Route path="/subscription-failed" element={<SubscriptionFailure />} />
      <Route path="/gift-cards" element={<GiftCardsPage />} />
      <Route path="/gift-card/:title/:id" element={<GiftCardDetailsComp />} />
      <Route path="/gift-card/select/:id" element={<GiftCardSelectComp />} />
      <Route
        path="/gift-card/enter-details/:id"
        element={<GiftCardEnterDetailsComp />}
      />
      <Route
        path="/gift-card/confirm-pay/:id"
        element={<GiftCardConfirmPayComp />}
      />
      <Route
        path="/gift-card/thank-you/:sessionId"
        element={<GiftCardThankYouComp />}
      />
      <Route path="/cancel" element={<GiftCardCancel />} />
      <Route path="/swoodleherosection" element={<Demosite />} />
      <Route path="/usersubscription" element={<UserSubscription />} />
      <Route path="/coming-soon" element={<ComingSoonPage />} />
      <Route
        path="/welcome-onboard/:verificationCode"
        element={<MerchantOnboardLayout element={<CreateAccount />} />}
      />
      <Route
        path="/account-created"
        element={<MerchantOnboardLayout element={<AccountCreated />} />}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default CommonRoutes;
